import React, { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatBRL } from '../lib/format';
import useTelaEstreita from '../lib/useTelaEstreita';

/* ----------------------------- OverviewPanel ----------------------------- */

export function OverviewPanel({ title, items, totalLabel, filterLabel, accentColor = '#10b981' }) {
  const [selectedType, setSelectedType] = useState('');

  // Get distinct types from items
  const types = useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (it.type) set.add(it.type); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [items]);

  const filtered = useMemo(() => {
    if (!selectedType) return items;
    return items.filter((i) => i.type === selectedType);
  }, [items, selectedType]);

  // Show only items with positive balance for the donut; total uses all (including negatives)
  const positive = filtered.filter((i) => Number(i.balance) > 0);
  const total = filtered.reduce((s, i) => s + (Number(i.balance) || 0), 0);
  const data = positive.map((i) => ({ name: i.name, value: Number(i.balance), color: i.color || '#6366f1' }));

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      </div>

      <div className="p-6">
        {types.length > 0 && (
          <div className="mb-6">
            <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">{filterLabel}:</div>
            <div className="flex flex-wrap gap-2">
              {types.map((t) => (
                <button key={t} onClick={() => setSelectedType(selectedType === t ? '' : t)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${selectedType === t ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                  {t}
                </button>
              ))}
              {selectedType && (
                <button onClick={() => setSelectedType('')}
                  className="px-3 py-1.5 rounded-lg text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30">
                  Limpar
                </button>
              )}
            </div>
          </div>
        )}

        <div className="text-center mb-2">
          <div className="text-sm text-slate-500 dark:text-slate-400">{totalLabel}</div>
          {/* No celular, R$ -123.456,78 em 4xl estourava a caixa */}
          <div className="text-2xl sm:text-4xl font-bold mt-1 break-words" style={{ color: total < 0 ? '#e11d48' : accentColor }}>{formatBRL(total)}</div>
        </div>

        {data.length === 0 ? (
          <div className="py-16 text-center text-slate-400 dark:text-slate-500 text-sm">Sem saldos positivos para exibir.</div>
        ) : (
          <DonutWithLabels data={data} total={total} />
        )}
      </div>
    </div>
  );
}

/* ------------------------ Donut with outside labels ----------------------- */

function DonutWithLabels({ data, total }) {
  // Compute percentage for each slice (against positive total)
  const positiveTotal = data.reduce((s, d) => s + d.value, 0) || 1;
  const dataWithPct = data.map((d) => ({ ...d, pct: Math.round((d.value / positiveTotal) * 100) }));

  /*
   * No celular este gráfico ficava cortado: as margens de 80px de cada lado e
   * o raio fixo de 120px somam 280px de largura mínima, e os rótulos ficavam
   * ainda mais para fora. Numa tela de 375px o desenho simplesmente não cabia.
   *
   * Em telas estreitas o donut encolhe e os rótulos externos saem — a legenda
   * embaixo já diz os nomes, e o toque na fatia mostra o valor.
   */
  const estreito = useTelaEstreita();
  const alturaGrafico = estreito ? 300 : 420;
  const margens = estreito
    ? { top: 8, right: 8, bottom: 8, left: 8 }
    : { top: 30, right: 80, bottom: 30, left: 80 };

  const renderLabel = ({ cx, cy, midAngle, outerRadius, name, color, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const pct = dataWithPct[index].pct;
    if (pct < 2) return null; // skip tiny slices
    return (
      <text x={x} y={y} fill={color} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central"
        style={{ fontSize: 12, fontWeight: 500 }}>
        {`${name} (${pct}%)`}
      </text>
    );
  };

  return (
    <div className="relative w-full">
      <ResponsiveContainer width="100%" height={alturaGrafico}>
        <PieChart margin={margens}>
          <Pie
            data={dataWithPct}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={estreito ? '72%' : 120}
            innerRadius={estreito ? '42%' : 68}
            paddingAngle={1}
            stroke="rgba(255,255,255,0.6)"
            strokeWidth={2}
            label={estreito ? false : renderLabel}
            labelLine={estreito ? false : { stroke: 'currentColor', strokeWidth: 1, strokeOpacity: 0.3 }}
            isAnimationActive
          >
            {dataWithPct.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <Tooltip
            formatter={(v, _n, item) => [formatBRL(v), item.payload.name]}
            contentStyle={{ background: 'rgb(30 41 59)', border: 'none', borderRadius: 8, color: 'white' }}
          />
          <Legend
            verticalAlign="bottom"
            iconType="square"
            wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
            formatter={(v) => <span className="text-slate-700 dark:text-slate-300">{v}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/* --------------------- Monthly Income/Expense Bar Chart -------------------- */

/* `expenseKey` escolhe qual despesa desenhar: `expense` (competência, o gasto
   quando foi decidido) ou `expense_cash` (caixa, o que saiu da conta). O mês
   de cada barra já vem agrupado pela mesma base, decidida no backend. */
export function MonthlyIncomeExpenseChart({ data, expenseKey = 'expense' }) {
  const estreito = useTelaEstreita();
  if (!data?.length) {
    return <div className="flex items-center justify-center h-[280px] text-slate-400 dark:text-slate-500 text-sm">Sem dados mensais</div>;
  }
  // No celular "2026-03" em seis colunas não cabe; "03/26" cabe.
  const mesCurto = (m) => (typeof m === 'string' && m.length === 7 ? `${m.slice(5)}/${m.slice(2, 4)}` : m);
  return (
    <ResponsiveContainer width="100%" height={estreito ? 240 : 280}>
      <BarChart data={data} margin={{ top: 10, right: 6, left: estreito ? -18 : 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
        <XAxis dataKey="month" tickFormatter={estreito ? mesCurto : undefined}
          tick={{ fontSize: estreito ? 9 : 11, fill: 'currentColor' }} interval={0}
          className="text-slate-500 dark:text-slate-400" />
        <YAxis tick={{ fontSize: estreito ? 9 : 11, fill: 'currentColor' }} width={estreito ? 44 : 60}
          className="text-slate-500 dark:text-slate-400" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
        <Tooltip formatter={(v) => formatBRL(v)} contentStyle={{ background: 'rgb(30 41 59)', border: 'none', borderRadius: 8, color: 'white' }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="income" name="Receitas" fill="#10b981" radius={[4,4,0,0]} />
        <Bar dataKey={expenseKey} name="Despesas" fill="#f43f5e" radius={[4,4,0,0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* Keep old export for backward compat (no longer used but harmless) */
export function PotsDistributionChart({ pots }) {
  return <OverviewPanel title="Visão Geral dos Potes" items={pots} totalLabel="Saldo Total em Potes" filterLabel="Filtrar por Tipo de Pote" />;
}
